import {
  ClientName,
  ShowNamesButton,
  useClientNamesVisibility,
} from '../../components/clientNames';
import { CopyOutlined } from '@ant-design/icons';
import { Invite, InviteType } from '@cc/schema';
import { CreateButton, DeleteButton, List, useTable } from '@refinedev/antd';
import { Button, Space, Table, Tooltip } from 'antd';

import {
  IResourceComponentsProps,
  useGetLocale,
  useTranslate,
} from '@refinedev/core';

import { format } from 'date-fns';
import { ClientsLimitReached } from '../../components/ClientsLimitReached';
import { SubscriptionExpired } from '../../components/SubscriptionExpired';
import { useSubscriptionWarnings } from '../../hooks';
import { PermanentInviteLinkBlock } from './PermanentInviteLinkBlock';

/**
 * E07 (M7) — `generateInviteLink` is now used ONLY for the per-invite links in the table below
 * (each row still carries its own one-shot token). The practice's client-invite link is no longer
 * built in the browser out of the therapist's row id: it is a server-minted, revocable token, and
 * `PermanentInviteLinkBlock` owns it end to end. See that file for why the old one had to go.
 */

export const generateInviteLink = (token: string, type: InviteType) => {
  if (type === InviteType.CLIENT) {
    return `https://copingcard.com/deep/join/${token}`;
  }

  return `https://portal.copingcard.com/join/${token}`;
};

export const InviteList: React.FC<IResourceComponentsProps> = () => {
  const translate = useTranslate();

  const locale = useGetLocale();

  const currentLocale = locale();

  const subWarnings = useSubscriptionWarnings();

  // Q-mt-3b A: the invitee's names show as initials (full word on hover) until Show names — 60 s
  const names = useClientNamesVisibility();

  const { tableProps } = useTable<Invite>({
    queryOptions: {
      select: (data) => {
        // @ts-ignore
        return { data: data.data.data, total: data.total };
      },
    },

    meta: {
      fields: [
        {
          data: [
            '_id',
            'createdAt',
            'expiresAt',
            'from',
            'type',
            'to',
            'token',
            'firstName',
            'lastName',
            'email',
            'message',
          ],
        },
        'count',
      ],
    },

    sorters: {
      initial: [
        {
          field: 'expiresAt',
          order: 'asc',
        },
      ],
    },
  });

  return (
    <List
      title={translate('invites.invites')}
      headerProps={{
        extra: (
          <Space wrap>
            <ShowNamesButton
              visible={names.visible}
              onToggle={names.toggle}
              testId="cc-invites-toggle-names"
            />
            <CreateButton
              data-testid="create-invite-button"
              disabled={
                subWarnings.limitReached || subWarnings.subscriptionExpired
              }
            />
          </Space>
        ),
      }}
    >
      <div
        style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}
        data-testid="cc-invite-list"
      >
        {!subWarnings.limitReached && <PermanentInviteLinkBlock />}
        <ClientsLimitReached />
        <SubscriptionExpired />
      </div>
      <Table {...tableProps} rowKey="_id">
        <Table.Column
          key="type"
          dataIndex="type"
          title={translate('invites.fields.type')}
          render={(text, record, indx) => {
            if (text !== 0 && !text) return undefined;
            return translate(`invites.types.${text}`);
          }}
        />
        <Table.Column
          key="firstName"
          dataIndex="firstName"
          title={translate('invites.fields.firstName')}
          render={(value?: string | null) =>
            value ? (
              <ClientName firstName={value} visible={names.visible} />
            ) : undefined
          }
        />
        <Table.Column
          key="lastName"
          dataIndex="lastName"
          title={translate('invites.fields.lastName')}
          render={(value?: string | null) =>
            value ? (
              <ClientName lastName={value} visible={names.visible} />
            ) : undefined
          }
        />
        <Table.Column
          key="createdAt"
          dataIndex="createdAt"
          title={translate('invites.fields.createdAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        <Table.Column
          key="expiresAt"
          dataIndex="expiresAt"
          title={translate('invites.fields.expiresAt')}
          sorter
          render={(text, record, indx) => {
            if (!text) return undefined;
            const date = new Date(text);
            return format(
              date,
              currentLocale?.startsWith('ru')
                ? 'yyyy-MM-dd HH:mm:ss'
                : 'yyyy-MM-dd hh:mm:ss aaa',
            );
          }}
        />
        {/* <Table.Column
          key="from"
          dataIndex="from"
          title={translate('invites.fields.from')}
        />
        <Table.Column
          key="to"
          dataIndex="to"
          title={translate('invites.fields.to')}
        /> */}
        <Table.Column
          key="token"
          dataIndex="token"
          title={translate('invites.fields.token')}
          render={(text, record, indx) => {
            return (
              <div>
                {text}{' '}
                <Tooltip title={translate('invites.copyToken')}>
                  <Button
                    onClick={() => navigator.clipboard.writeText(text)}
                    style={{ marginLeft: '5px' }}
                    icon={<CopyOutlined />}
                  />
                </Tooltip>
              </div>
            );
          }}
        />
        <Table.Column
          key="email"
          dataIndex="email"
          title={translate('invites.fields.email')}
          sorter
        />
        <Table.Column<Invite>
          title={translate('resourcesCommon.actions')}
          dataIndex="actions"
          render={(_, record) => (
            <Space>
              <Button
                size="small"
                icon={<CopyOutlined />}
                title={translate('invites.fields.copy')}
                onClick={() => {
                  const text = generateInviteLink(record.token, record.type);
                  navigator.clipboard.writeText(text);
                }}
              >
                {translate('invites.fields.copy')}
              </Button>
              <DeleteButton
                meta={{
                  fields: ['_id'],
                }}
                hideText
                size="small"
                recordItemId={record._id}
              />
            </Space>
          )}
        />
      </Table>
    </List>
  );
};
